type SiteLike = {
  id?: string | number;
  titulo?: string;
  meta_description?: string;
  transacoes?: string[];
  Dados?: {
    empresa?: string;
    cidade?: string;
  };
};

type NamedItem = {
  nome?: string;
  label?: string;
};

type ImoveisSeoOverrides = {
  transaction?: string;
  city?: string;
  districts?: string[];
  types?: string[];
  company?: string;
  category?: string;
  code?: string;
  dormitorios?: unknown;
  suites?: unknown;
  banheiros?: unknown;
  vagas?: unknown;
  promocao?: boolean;
  favoritos?: boolean;
};

type ImoveisSeoInput = {
  asPath?: string;
  site?: SiteLike | null;
  allDataHome?: {
    cidades?: NamedItem[];
    tipos?: NamedItem[];
  } | null;
  query?: Record<string, unknown>;
  overrides?: ImoveisSeoOverrides;
};

type ParsedPath = {
  transaction: string;
  city: string;
  districts: string[];
  company: string;
  types: string[];
  code: string;
  isNovidades: boolean;
};

const TRANSACTION_TEXT: Record<string, string> = {
  venda: 'à venda',
  aluguel: 'para alugar',
  temporada: 'para temporada',
};

const SMALL_WORDS = [
  'a',
  'o',
  'as',
  'os',
  'de',
  'da',
  'do',
  'das',
  'dos',
  'e',
  'em',
  'no',
  'na',
  'nos',
  'nas',
  'para',
  'por',
  'com',
];

const cleanText = (value: unknown) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const normalizeLookup = (value: unknown) =>
  cleanText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const toTitleCase = (value: string) => {
  const normalized = cleanText(value).toLowerCase();
  if (!normalized) return '';

  return normalized
    .split(' ')
    .map((word, index) => {
      if (index > 0 && SMALL_WORDS.includes(word)) return word;
      return word.charAt(0).toLocaleUpperCase('pt-BR') + word.slice(1);
    })
    .join(' ');
};

const decodeSlugLabel = (value: string) =>
  toTitleCase(
    safeDecode(value)
      .replace(/---/g, '-002D-')
      .replace(/-/g, ' ')
      .replace(/002D/gi, '-')
      .replace(/\+/g, ' ')
  );

const labelFromKnownList = (label: string, knownItems?: NamedItem[]) => {
  const key = normalizeLookup(label);
  if (!key || !Array.isArray(knownItems)) return '';

  const found = knownItems.find((item) => {
    const itemLabel = item?.nome || item?.label || '';
    return normalizeLookup(itemLabel) === key;
  });

  return cleanText(found?.nome || found?.label || '');
};

const labelsFromSegment = (segment: string | undefined, knownItems?: NamedItem[]) => {
  if (!segment || segment === '-') return [];

  return segment
    .split('+')
    .map((part) => {
      const decoded = decodeSlugLabel(part);
      return labelFromKnownList(decoded, knownItems) || decoded;
    })
    .filter(Boolean);
};

const normalizeTransaction = (value: unknown, site?: SiteLike | null) => {
  const raw = normalizeLookup(value);
  if (raw === 'aluguel' || raw === 'locacao' || raw === 'locação' || raw === 'rent') return 'aluguel';
  if (raw === 'temporada') return 'temporada';
  if (raw === 'venda' || raw === 'sale') return 'venda';

  const firstTransaction = normalizeLookup(site?.transacoes?.[0]);
  if (firstTransaction === 'aluguel' || firstTransaction === 'locacao' || firstTransaction === 'locação') {
    return 'aluguel';
  }
  if (firstTransaction === 'temporada') return 'temporada';
  return 'venda';
};

const parsePath = (asPath: string | undefined, allDataHome?: ImoveisSeoInput['allDataHome'], site?: SiteLike | null): ParsedPath => {
  const path = String(asPath || '/imoveis').split('?')[0].split('#')[0];
  const parts = path.split('/').filter(Boolean);
  const model = parts[1] || '';
  const isNovidades = model === 'novidades';

  return {
    transaction: normalizeTransaction(model, site),
    city: labelsFromSegment(parts[2], allDataHome?.cidades)[0] || '',
    districts: labelsFromSegment(parts[3]),
    company: labelsFromSegment(parts[4])[0] || '',
    types: labelsFromSegment(parts[5], allDataHome?.tipos),
    code: labelsFromSegment(parts[6])[0] || '',
    isNovidades,
  };
};

const normalizeLabelList = (value: string[] | undefined) =>
  Array.isArray(value) ? value.map((item) => cleanText(item)).filter(Boolean) : [];

const firstClean = (...values: unknown[]) => {
  for (const value of values) {
    const cleaned = cleanText(value);
    if (cleaned) return cleaned;
  }
  return '';
};

const formatList = (values: string[]) => {
  const list = values.map((item) => cleanText(item)).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} e ${list[1]}`;
  return `${list[0]}, ${list[1]} e ${list[2]}`;
};

const pluralizeType = (value: string) => {
  const label = cleanText(value);
  if (!label) return '';

  return label
    .replace(/^Casa\b/i, 'Casas')
    .replace(/^Apartamento\b/i, 'Apartamentos')
    .replace(/^Terreno\b/i, 'Terrenos')
    .replace(/^Sobrado\b/i, 'Sobrados')
    .replace(/^Cobertura\b/i, 'Coberturas')
    .replace(/^Sala\b/i, 'Salas')
    .replace(/^Loft\b/i, 'Lofts')
    .replace(/^Chácara\b/i, 'Chácaras')
    .replace(/^Fazenda\b/i, 'Fazendas')
    .replace(/^Lote\b/i, 'Lotes');
};

const getBrandName = (site?: SiteLike | null) => {
  const candidates = [site?.titulo, site?.Dados?.empresa].map(cleanText).filter(Boolean);
  const withImobiliaria = candidates.find((item) => /imobili[aá]ri/i.test(item));
  const source = withImobiliaria || candidates[0] || 'Imobiliária';

  if (/imobili[aá]ri/i.test(source)) return source;
  return `Imobiliária ${source}`;
};

const getQueryValue = (
  key: string,
  query?: Record<string, unknown>,
  searchParams?: URLSearchParams,
  override?: unknown
) => {
  if (override !== undefined && override !== null && override !== '') return override;
  const fromQuery = query?.[key];
  if (Array.isArray(fromQuery)) return fromQuery[0];
  if (fromQuery !== undefined && fromQuery !== null && fromQuery !== '') return fromQuery;
  return searchParams?.get(key) || '';
};

const toPositiveInt = (value: unknown) => {
  const source = Array.isArray(value) ? value[0] : value;
  const parsed = Number(String(source || '').replace(/[^\d]/g, ''));
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
};

const getMainFeatureText = (
  query?: Record<string, unknown>,
  searchParams?: URLSearchParams,
  overrides?: ImoveisSeoOverrides
) => {
  const dormitorios = toPositiveInt(getQueryValue('dormitorios', query, searchParams, overrides?.dormitorios));
  const suites = toPositiveInt(getQueryValue('suites', query, searchParams, overrides?.suites));
  const banheiros = toPositiveInt(getQueryValue('banheiros', query, searchParams, overrides?.banheiros));
  const vagas = toPositiveInt(getQueryValue('vagas', query, searchParams, overrides?.vagas));

  if (dormitorios) return `com ${dormitorios} ${dormitorios === 1 ? 'dormitório' : 'dormitórios'}`;
  if (suites) return `com ${suites} ${suites === 1 ? 'suíte' : 'suítes'}`;
  if (banheiros) return `com ${banheiros} ${banheiros === 1 ? 'banheiro' : 'banheiros'}`;
  if (vagas) return `com ${vagas} ${vagas === 1 ? 'vaga' : 'vagas'}`;
  return '';
};

const decapitalize = (value: string) => {
  const cleaned = cleanText(value);
  if (!cleaned) return '';
  return cleaned.charAt(0).toLocaleLowerCase('pt-BR') + cleaned.slice(1);
};

const limitMetaDescription = (value: string, maxLength = 180) => {
  const cleaned = cleanText(value);
  if (cleaned.length <= maxLength) return cleaned;

  const sliced = cleaned.slice(0, maxLength - 1);
  const lastSpace = sliced.lastIndexOf(' ');
  const safeSlice = lastSpace > 120 ? sliced.slice(0, lastSpace) : sliced;
  return `${safeSlice.replace(/[,.]$/, '')}.`;
};

export const buildImoveisSeo = ({
  asPath,
  site,
  allDataHome,
  query,
  overrides,
}: ImoveisSeoInput) => {
  const parsed = parsePath(asPath, allDataHome, site);
  const searchParams = new URLSearchParams(String(asPath || '').split('?')[1] || '');
  const brand = getBrandName(site);

  if (parsed.isNovidades) {
    const heading = 'Novidades em imóveis';
    return {
      title: `${heading} | ${brand}`,
      heading,
      description: limitMetaDescription(
        `Confira novidades em imóveis com a ${brand}. Veja oportunidades recentes, compare opções por cidade, bairro e valor, e fale com um corretor especialista.`
      ),
      intro: `Confira novidades em imóveis com opções recentes para morar ou investir. A ${brand} reúne oportunidades por localização, valor e características para facilitar sua escolha.`,
    };
  }

  const transaction = normalizeTransaction(overrides?.transaction || parsed.transaction, site);
  const transactionText = TRANSACTION_TEXT[transaction] || TRANSACTION_TEXT.venda;
  const city = firstClean(overrides?.city, parsed.city);
  const districts = normalizeLabelList(overrides?.districts);
  const selectedDistricts = districts.length > 0 ? districts : parsed.districts;
  const types = normalizeLabelList(overrides?.types);
  const selectedTypes = types.length > 0 ? types : parsed.types;
  const company = firstClean(overrides?.company, parsed.company);
  const category = firstClean(overrides?.category);
  const code = firstClean(overrides?.code, parsed.code);
  const isPromocao = Boolean(overrides?.promocao || getQueryValue('promocao', query, searchParams) === '1');
  const isFavoritos = Boolean(overrides?.favoritos || getQueryValue('favoritos', query, searchParams));

  if (code) {
    const heading = `Imóvel código ${code}`;
    return {
      title: `${heading} | ${brand}`,
      heading,
      description: limitMetaDescription(
        `Veja informações do imóvel código ${code} com a ${brand}. Consulte disponibilidade, localização, valor e fale com um corretor especialista.`
      ),
      intro: `Consulte as informações do imóvel código ${code}, compare características e fale com a ${brand} para confirmar disponibilidade, valor e condições de negociação.`,
    };
  }

  if (isFavoritos) {
    const heading = 'Imóveis favoritos';
    return {
      title: `${heading} | ${brand}`,
      heading,
      description: limitMetaDescription(
        `Veja sua seleção de imóveis favoritos na ${brand} e compare opções por localização, valor, dormitórios e características.`
      ),
      intro: `Veja sua seleção de imóveis favoritos e compare opções por localização, valor, dormitórios e características antes de falar com a ${brand}.`,
    };
  }

  const subjectBase = selectedTypes.length > 0
    ? formatList(selectedTypes.map(pluralizeType))
    : 'Imóveis';
  const subject = category ? `${subjectBase} ${category}` : subjectBase;
  const districtText = formatList(selectedDistricts);
  const locationParts = [
    company ? `no empreendimento ${company}` : '',
    districtText ? `${selectedDistricts.length > 1 ? 'nos bairros' : 'no bairro'} ${districtText}` : '',
    city ? `em ${city}` : '',
  ].filter(Boolean);
  const location = locationParts.join(', ');
  const featureText = getMainFeatureText(query, searchParams, overrides);

  const heading = cleanText(
    `${isPromocao ? `${subject} em promoção` : `${subject} ${transactionText}`} ${location} ${featureText}`
  );
  const subjectDescription = decapitalize(subject);
  const searchIntent = cleanText(
    `${subjectDescription} ${isPromocao ? 'em promoção' : transactionText} ${location}`
  );
  const filterLocationText = city || districtText ? 'bairro, valor e dormitórios' : 'cidade, bairro, valor e dormitórios';
  const intro = Number(site?.id) === 1630
    ? 'Confira imóveis à venda no Sul da Ilha para morar ou investir com a Prospect Imóveis. Encontre oportunidades em diferentes regiões, com filtros inteligentes por valor, localização, número de dormitórios, condomínios e loteamentos — tudo para tornar sua busca mais prática, rápida e assertiva.'
    : cleanText(
      `Confira ${searchIntent} para morar ou investir. A ${brand} reúne imóveis em diferentes localidades, com filtros por valor, localização, dormitórios e características para facilitar sua escolha.`
    );

  return {
    title: `${heading} | ${brand}`,
    heading,
    description: limitMetaDescription(
      `Encontre ${searchIntent} com a ${brand}. Veja imóveis selecionados, compare opções por ${filterLocationText} e fale com um corretor especialista.`
    ),
    intro,
  };
};
